import type {
  ColumnFilter,
  FilterCondition,
  FilterDataType,
  FilterModel,
  FilterOperator,
  FilterableColumn,
  SortModel,
} from './types';

export interface OperatorDefinition {
  operator: FilterOperator;
  label: string;
  inputs: 0 | 1 | 2;
}

export const TEXT_OPERATORS: OperatorDefinition[] = [
  { operator: 'equals', label: 'Equals To', inputs: 1 },
  { operator: 'neq', label: 'Does Not Equals To', inputs: 1 },
  { operator: 'startswith', label: 'Starts With', inputs: 1 },
  { operator: 'contains', label: 'Contains', inputs: 1 },
  { operator: 'notcontains', label: 'Does Not Contains', inputs: 1 },
];

export const NUMBER_OPERATORS: OperatorDefinition[] = [
  { operator: 'equals', label: 'Equals To', inputs: 1 },
  { operator: 'gt', label: 'Greater Than', inputs: 1 },
  { operator: 'lt', label: 'Less Than', inputs: 1 },
  { operator: 'between', label: 'Between', inputs: 2 },
];

export const OPERATORS_BY_TYPE: Record<FilterDataType, OperatorDefinition[]> = {
  text: TEXT_OPERATORS,
  number: NUMBER_OPERATORS,
  boolean: [],
  enum: TEXT_OPERATORS,
  date: [],
};

export const SUBMENU_LABEL_BY_TYPE: Record<FilterDataType, string> = {
  text: 'Text Filters',
  number: 'Number Filters',
  boolean: 'Text Filters',
  enum: 'Text Filters',
  date: 'Date Filters',
};

export function getSubmenuLabel(type: FilterDataType): string {
  return SUBMENU_LABEL_BY_TYPE[type] ?? 'Text Filters';
}

export function getOperators(type: FilterDataType): OperatorDefinition[] {
  return OPERATORS_BY_TYPE[type] ?? [];
}

export function getOperatorDefinition(
  type: FilterDataType,
  operator: FilterOperator
): OperatorDefinition | undefined {
  return getOperators(type).find((o) => o.operator === operator);
}

export function describeCondition(
  type: FilterDataType,
  operator: FilterOperator,
  value1?: string | number | null,
  value2?: string | number | null
): string {
  const def = getOperatorDefinition(type, operator);
  const label = def?.label ?? operator;
  if (def?.inputs === 0) return label.toLowerCase();
  if (def?.inputs === 2) return `${label.toLowerCase()} ${value1 ?? ''} and ${value2 ?? ''}`;
  return `${label.toLowerCase()} ${value1 ?? ''}`.trim();
}

export function inputTypeFor(type: FilterDataType): 'text' | 'number' {
  if (type === 'number') return 'number';
  return 'text'; // already covers 'enum' — no change needed here, just confirming
}

export function getCellValue(row: any, column: FilterableColumn): any {
  if (column.getFilterValue) return column.getFilterValue(row);
  return row?.[column.key];
}

export function isBlank(value: any): boolean {
  return value === null || value === undefined || value === '' || value === '--';
}

function toComparableText(value: any): string {
  return String(value ?? '').trim().toLowerCase();
}

function toNumber(value: any): number | null {
  if (isBlank(value)) return null;
  const n = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
  return Number.isNaN(n) ? null : n;
}

function normalizeDataType(value: unknown): FilterDataType | null {
  if (typeof value !== 'string') return null;

  const type = value.trim().toLowerCase();
  if (['date', 'datetime', 'timestamp', 'time'].includes(type)) return 'date';
  if (['number', 'numeric', 'integer', 'int', 'float', 'decimal', 'double'].includes(type)) return 'number';
  if (['boolean', 'bool'].includes(type)) return 'boolean';
  if (['enum', 'choice', 'select', 'dropdown', 'multiselect'].includes(type)) return 'enum';
  if (['text', 'string', 'varchar', 'nvarchar', 'char', 'uuid', 'email'].includes(type)) return 'text';

  return null;
}

export function resolveDataType(column: FilterableColumn): FilterDataType | null {
  const metadata = (column as any).columnDetails;
  if (metadata && typeof metadata === 'object') {
    const candidates = new Set<string>([
      column.key,
      String(column.key).toLowerCase(),
      column.filterField ?? '',
      String(column.filterField ?? '').toLowerCase(),
    ]);

    for (const candidate of candidates) {
      const value = (metadata as Record<string, any>)[candidate];
      const normalized = normalizeDataType(value);
      if (normalized) return normalized;
    }
  }

  return null;
}

export function inferFilterType(rows: any[], column: FilterableColumn): FilterDataType {
  const metadataType = resolveDataType(column);
  if (metadataType) return metadataType;

  let sampled = 0;
  let numeric = 0;
  let boolean = 0;

  for (const row of rows) {
    const value = getCellValue(row, column);
    if (isBlank(value)) continue;
    sampled += 1;
    if (typeof value === 'boolean') boolean += 1;
    else if (toNumber(value) !== null) numeric += 1;
    if (sampled >= 50) break;
  }

  if (sampled === 0) return 'text';
  if (boolean === sampled) return 'boolean';
  if (numeric === sampled) return 'number';
  return 'text';
}

export function matchesCondition(
  value: any,
  condition: FilterCondition,
  type: FilterDataType,
  column?: FilterableColumn
): boolean {
  const { operator, value1, value2 } = condition;

  if (type === 'number') {
    const a = toNumber(value);
    const b = toNumber(value1);
    const c = toNumber(value2);
    if (a === null) return false;
    switch (operator) {
      case 'equals': return b !== null && a === b;
      case 'gt': return b !== null && a > b;
      case 'lt': return b !== null && a < b;
      case 'between': {
        if (b === null || c === null) return true;
        const [lo, hi] = b <= c ? [b, c] : [c, b];
        return a >= lo && a <= hi;
      }
      default: return true;
    }
  }

  // text / boolean / enum
  const a = toComparableText(value);
  const b = toComparableText(value1);
  if (b === '') return true;
  switch (operator) {
    case 'equals': return a === b;
    case 'neq': return a !== b;
    case 'startswith': return a.startsWith(b);
    case 'contains': return a.includes(b);
    case 'notcontains': return !a.includes(b);
    default: return true;
  }
}

function matchesValueList(value: any, values: ColumnFilter['values']): boolean {
  if (!values || values.length === 0) return true;
  const normalized = toComparableText(value);
  return values.some((v) => toComparableText(v) === normalized);
}

export function isColumnFilterActive(filter?: ColumnFilter | null): boolean {
  if (!filter) return false;
  const hasValues = Array.isArray(filter.values) && filter.values.length > 0;
  const hasCondition = Boolean(filter.condition);
  return hasValues || hasCondition;
}

export function countFilterParts(filter?: ColumnFilter | null): number {
  if (!filter) return 0;
  const hasSelectedValues = Array.isArray(filter.values) && filter.values.length > 0;
  const hasCondition = Boolean(filter.condition);
  return Number(hasSelectedValues) + Number(hasCondition);
}

export function isRowMatch(
  row: any,
  filterModel: FilterModel,
  columns: FilterableColumn[],
  rowsForInference: any[] = [],
  skipColumnKey?: string
): boolean {
  // AND across every column that carries an active filter.
  for (const column of columns) {
    if (skipColumnKey && column.key === skipColumnKey) continue;
    const filter = filterModel[column.key];
    if (!isColumnFilterActive(filter)) continue;

    const value = getCellValue(row, column);
    if (!matchesValueList(value, filter!.values)) return false;

    if (filter!.condition) {
      const type = inferFilterType(rowsForInference, column);
      if (!matchesCondition(value, filter!.condition, type, column)) return false;
    }
  }
  return true;
}

/** Client-side filtering. Server-side grids ignore this and send the model to the API. */
export function applyFilters(
  rows: any[],
  filterModel: FilterModel,
  columns: FilterableColumn[]
): any[] {
  const active = columns.some((c) => isColumnFilterActive(filterModel[c.key]));
  if (!active) return rows;
  return rows.filter((row) => isRowMatch(row, filterModel, columns, rows));
}

export function getDistinctValues(
  rows: any[],
  column: FilterableColumn,
  filterModel: FilterModel,
  columns: FilterableColumn[],
  limit = 1000
): Array<string | number | boolean | null> {
  const seen = new Map<string, any>();
  for (const row of rows) {
    if (!isRowMatch(row, filterModel, columns, rows, column.key)) continue;
    const raw = getCellValue(row, column);
    const key = toComparableText(raw);
    if (!seen.has(key)) seen.set(key, raw);
    if (seen.size >= limit) break;
  }

  const type = inferFilterType(rows, column);
  const values = [...seen.values()];
  values.sort((a, b) => {
    if (isBlank(a)) return 1;
    if (isBlank(b)) return -1;
    if (type === 'number') return (toNumber(a) ?? 0) - (toNumber(b) ?? 0);
    return toComparableText(a).localeCompare(toComparableText(b));
  });
  return values;
}

function compareValues(a: any, b: any, type: FilterDataType, column: FilterableColumn): number {
  const aBlank = isBlank(a);
  const bBlank = isBlank(b);
  if (aBlank && bBlank) return 0;
  if (aBlank) return 1;   // blanks always sink, regardless of direction
  if (bBlank) return -1;

  if (type === 'number') return (toNumber(a) ?? 0) - (toNumber(b) ?? 0);
  if (type === 'boolean') return Number(a) - Number(b);
  return toComparableText(a).localeCompare(toComparableText(b), undefined, { numeric: true });
}

export function applySort(
  rows: any[],
  sortModel: SortModel,
  columns: FilterableColumn[]
): any[] {
  if (!sortModel.length) return rows;

  const byKey = new Map(columns.map((c) => [c.key, c]));
  const decorated = rows.map((row, index) => ({ row, index }));

  decorated.sort((left, right) => {
    for (const { columnKey, direction } of sortModel) {
      const column = byKey.get(columnKey);
      if (!column) continue;
      const type = inferFilterType(rows, column);
      const result = compareValues(
        getCellValue(left.row, column),
        getCellValue(right.row, column),
        type,
        column
      );
      if (result !== 0) return direction === 'asc' ? result : -result;
    }
    return left.index - right.index; // stability
  });

  return decorated.map((d) => d.row);
}